import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ChatProvider, useChat } from './chat-provider';
import { ChatHistoryPanel } from './components/chat-sidebar';
import { ChatWindow } from './components/chat-window';
import { CanvasPane, type CanvasArtifact } from './components/canvas-pane';
import { cn } from '@/lib/utils';
import { Menu, X } from 'lucide-react';

function ChatPageInner() {
    const { conversationId } = useParams<{ conversationId?: string }>();
    const navigate = useNavigate();
    const { setActiveConversation, activeConversationId } = useChat();
    const [canvasArtifact, setCanvasArtifact] = useState<CanvasArtifact | null>(null);
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);

    useEffect(() => {
        if (conversationId && conversationId !== activeConversationId) {
            setActiveConversation(conversationId);
        }
    }, [conversationId]);

    useEffect(() => {
        if (activeConversationId && !conversationId) {
            navigate(`/chat/${activeConversationId}`, { replace: true });
        } else if (!activeConversationId && conversationId) {
            navigate('/chat', { replace: true });
        }
    }, [activeConversationId, conversationId, navigate]);

    const handleCloseCanvas = () => setCanvasArtifact(null);

    return (
        <div className="flex h-[calc(100vh-4rem)] px-4">
            <div className="relative flex h-full min-h-0 flex-1 gap-4 overflow-hidden rounded-lg bg-slate-800/50 p-4 shadow-sm sm:p-5">
                {/* Chat panel */}
                <div
                    className={cn(
                        'flex min-w-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-300 ease-in-out dark:border-slate-700 dark:bg-slate-900',
                        canvasArtifact ? 'flex-[3]' : 'flex-1',
                    )}
                >
                    <ChatWindow
                        onOpenCanvas={setCanvasArtifact}
                    />
                </div>

                {/* History sidebar — hidden on mobile, hide when canvas open on desktop */}
                <div
                    className={cn(
                        'hidden flex-col overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-300 ease-in-out dark:border-slate-700 dark:bg-slate-900 md:flex',
                        canvasArtifact ? 'w-0 border-0 p-0 opacity-0' : 'w-[20%] min-w-[220px]',
                    )}
                >
                    <ChatHistoryPanel />
                </div>

                {/* Canvas pane */}
                {canvasArtifact && (
                    <div className="flex flex-[4] overflow-hidden transition-all duration-300 ease-in-out">
                        <CanvasPane
                            artifact={canvasArtifact}
                            onClose={handleCloseCanvas}
                        />
                    </div>
                )}

                {/* Mobile hamburger */}
                <button
                    className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-lg bg-slate-700/50 text-white md:hidden"
                    onClick={() => setShowMobileSidebar(true)}
                >
                    <Menu className="h-4 w-4" />
                </button>

                {/* Mobile sidebar overlay */}
                {showMobileSidebar && (
                    <>
                        <div
                            className="absolute inset-0 z-10 bg-black/20 md:hidden"
                            onClick={() => setShowMobileSidebar(false)}
                        />
                        <div className="absolute inset-y-0 right-0 z-20 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg md:hidden dark:border-slate-700 dark:bg-slate-900">
                            <div className="flex items-center justify-end p-2">
                                <button
                                    onClick={() => setShowMobileSidebar(false)}
                                    className="rounded p-1 text-slate-400 hover:text-slate-600"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <ChatHistoryPanel />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default function ChatPage() {
    return (
        <ChatProvider>
            <ChatPageInner />
        </ChatProvider>
    );
}
